import { Button, type ButtonProps, IconButton } from "@mui/material";
import { useState } from "react";
import inflection from "inflection";
import {
  useRecordContext,
  RaRecord,
  Confirm,
  DeleteWithConfirmButtonProps,
  useTranslate,
  useResourceContext,
} from "react-admin";

interface Props extends ButtonProps {
  callback?: (record: RaRecord) => void;
  icon?: JSX.Element;
  isHidden?: (record) => boolean;
}

function CustomActionButton({
  callback: action,
  icon,
  children,
  isHidden,
  ...buttonProps
}: Props) {
  const record = useRecordContext();
  const hidden = isHidden?.(record);
  if (hidden) {
    return null;
  }

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    return action?.(record);
  }

  if (!icon) {
    return (
      <Button
        {...buttonProps}
        onClick={action ? () => action(record) : undefined}
      >
        {children}
      </Button>
    );
  }

  return (
    <IconButton {...buttonProps} size="small" onClick={(e) => handleClick(e)}>
      {icon}
    </IconButton>
  );
}

interface PropsWithDelete extends DeleteWithConfirmButtonProps<any> {
  callback?: (record: RaRecord) => void;
  icon?: JSX.Element;
  isHidden?: (record) => boolean;
}

export function CustomActionButtonWithConfirm({
  callback: action,
  icon,
  children,
  isHidden,
  confirmTitle = "ra.message.delete_title",
  confirmContent = "ra.message.delete_content",
  confirmColor = "primary",
  translateOptions = {},
  ...buttonProps
}: PropsWithDelete) {
  const record = useRecordContext();
  const t = useTranslate();
  const hidden = isHidden?.(record);
  const resource = useResourceContext(buttonProps);
  const [open, setOpen] = useState(false);

  if (hidden) {
    return null;
  }

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  }
  function handleDelete(e: React.MouseEvent<HTMLButtonElement>) {
    setOpen(false);
    return action?.(record);
  }

  if (!icon) {
    return (
      <Button
        {...buttonProps}
        onClick={action ? () => action(record) : undefined}
      >
        {children}
      </Button>
    );
  }

  return (
    <>
      <IconButton {...buttonProps} size="small" onClick={(e) => handleClick(e)}>
        {icon}
      </IconButton>
      <Confirm
        isOpen={open}
        title={confirmTitle}
        content={confirmContent}
        confirmColor={confirmColor}
        translateOptions={{
          name: t(`resources.${resource}.forcedCaseName`, {
            smart_count: 1,
            _: inflection.humanize(
              t(`resources.${resource}.name`, {
                smart_count: 1,
                _: inflection.singularize(resource),
              }),
              true,
            ),
          }),
          id: record?.id,
          ...translateOptions,
        }}
        onConfirm={handleDelete}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

export default CustomActionButton;
